import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import api from "../lib/api";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function InsightsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageWrapper testId="insights-page">
      <PageHero
        eyebrow="Insights"
        title="Editorial perspectives from the institutional frontier."
        description="Long-form thinking on the architecture, exposure, and execution reshaping global education."
      />

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          {loading ? (
            <div className="flex items-center gap-3 text-slate-500">
              <Loader2 className="animate-spin" size={18} /> Loading insights…
            </div>
          ) : posts.length === 0 ? (
            <div className="text-slate-500">
              Insights are being curated. Check back shortly.
            </div>
          ) : (
            <>
              <Link
                to={`/insights/${posts[0].slug}`}
                data-testid="insights-featured"
                className="card-lift group block bg-white border border-slate-200 overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {posts[0].cover_image ? (
                    <div className="aspect-[4/3] lg:aspect-auto bg-slate-100 overflow-hidden">
                      <img
                        src={posts[0].cover_image}
                        alt={posts[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      />
                    </div>
                  ) : null}
                  <div className="p-8 sm:p-12 flex flex-col justify-center">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                      Featured · {posts[0].category}
                    </div>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl text-navy font-light leading-tight tracking-tight">
                      {posts[0].title}
                    </h2>
                    <p className="mt-5 text-slate-600 leading-relaxed">
                      {posts[0].excerpt}
                    </p>
                    <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-navy">
                      Read insight
                      <ArrowUpRight
                        size={16}
                        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </div>
                  </div>
                </div>
              </Link>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(1).map((p) => (
                  <Link
                    key={p.id}
                    to={`/insights/${p.slug}`}
                    data-testid={`insights-card-${p.slug}`}
                    className="card-lift group block bg-white border border-slate-200"
                  >
                    {p.cover_image ? (
                      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                        />
                      </div>
                    ) : null}
                    <div className="p-7">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                        {p.category}
                      </div>
                      <h3 className="mt-3 font-display text-xl leading-tight text-navy">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-slate-600 line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-navy">
                        Read insight <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </PageWrapper>
  );
}
